import { Vector } from "@animech/axiom-format";
import { vectorToVec3 } from "@animech/axiom-utility";
import { assert } from "@animech/core-utilities";
import RAPIER from "@dimforge/rapier3d";
import {
	AppBase,
	BoundingBox,
	Color,
	Entity,
	GraphNode,
	ModelComponent,
	Quat,
	Ray,
	RenderComponent,
	Vec3,
} from "playcanvas";
import { getRotation } from "src/utility";

export type BodyType = "dynamic" | "kinematic" | "fixed";

export type BodyTypeCollisionPair =
	| "dynamic:dynamic"
	| "dynamic:kinematic"
	| "dynamic:fixed"
	| "kinematic:kinematic"
	| "kinematic:fixed"
	| "fixed:fixed";

const rapierBodyType: Record<BodyType, RAPIER.RigidBodyType> = {
	dynamic: RAPIER.RigidBodyType.Dynamic,
	fixed: RAPIER.RigidBodyType.Fixed,
	kinematic: RAPIER.RigidBodyType.KinematicPositionBased,
};

const rapierActiveCollisionType: Record<BodyTypeCollisionPair, number> = {
	"dynamic:dynamic": RAPIER.ActiveCollisionTypes.DYNAMIC_DYNAMIC,
	"dynamic:kinematic": RAPIER.ActiveCollisionTypes.DYNAMIC_KINEMATIC,
	"dynamic:fixed": RAPIER.ActiveCollisionTypes.DYNAMIC_FIXED,
	"kinematic:kinematic": RAPIER.ActiveCollisionTypes.KINEMATIC_KINEMATIC,
	"kinematic:fixed": RAPIER.ActiveCollisionTypes.KINEMATIC_FIXED,
	"fixed:fixed": RAPIER.ActiveCollisionTypes.FIXED_FIXED,
};

interface ColliderBaseDefinition {
	rotation?: Quat;
	translation?: Vec3;
}

export interface BoxColliderDefinition extends ColliderBaseDefinition {
	type: "box";
	halfExtents: Vector;
}

export interface SphereColliderDefinition extends ColliderBaseDefinition {
	type: "sphere";
	radius: number;
}

export interface CapsuleColliderDefinition extends ColliderBaseDefinition {
	type: "capsule";
	/** The half-height of the capsule, along the `y` axis. */
	halfHeight: number;
	/** The radius of the capsule basis. */
	radius: number;
}

export interface CylinderColliderDefinition extends ColliderBaseDefinition {
	type: "cylinder";
	/** The half-height of the cylinder, along the `y` axis. */
	halfHeight: number;
	/** The radius of the cylinder basis. */
	radius: number;
}

export interface ConeColliderDefinition extends ColliderBaseDefinition {
	type: "cone";
	/** The half-height of the cone, along the `y` axis. */
	halfHeight: number;
	/** The radius of the cone basis. */
	radius: number;
}

export interface PlaneColliderDefinition extends ColliderBaseDefinition {
	type: "plane";
	normal: Vector;
}

export type ColliderDefinition =
	| BoxColliderDefinition
	| SphereColliderDefinition
	| CapsuleColliderDefinition
	| CylinderColliderDefinition
	| ConeColliderDefinition
	| PlaneColliderDefinition;

export type ColliderDefinitionType = ColliderDefinition["type"];

export type OnCollidingEntitiesUpdated = () => void;

export type OnValidateEntityCollision = (
	entityA: Entity,
	entityB: Entity,
) => boolean;

export type RayCastHit = {
	entity: Entity;
	normal: Vec3;
	position: Vec3;
	distance: number;
};

export interface ColliderMeta {
	definitionType: ColliderDefinitionType;
}

export interface EntityContact {
	/**
	 * Distance between the two contact points.
	 * If this is negative, this contact represents a penetration.
	 */
	distance: number;

	/** Entity that contacts were requested for. */
	entity: Entity;
	/** Position of the contact within `entity`. */
	point: Vec3;
	/** Contact normal, pointing towards the exterior of `entity`. */
	normal: Vec3;
	/** Meta data about the collider on `entity` that is involved in the contact. */
	colliderMeta: ColliderMeta;

	/** Other entity involved in the contact. */
	otherEntity: Entity;
	/** Position of the contact within `otherEntity`. */
	otherPoint: Vec3;
	/** Contact normal, pointing towards the exterior of `otherEntity`. */
	otherNormal: Vec3;
	/** Meta data about the collider on `otherEntity` that is involved in the contact. */
	otherColliderMeta: ColliderMeta;
}

interface CollisionPair extends ColliderHandlePair {
	b1: RAPIER.RigidBody;
	b2: RAPIER.RigidBody;
	contact?: RAPIER.ShapeContact;
}

interface ColliderHandlePair {
	h1: RAPIER.ColliderHandle;
	h2: RAPIER.ColliderHandle;
}

interface Connection {
	body: RAPIER.RigidBody;
	entity: Entity;
	onDestroyHandler(): void;
}

function exhaustiveCheck(x: never): never {
	throw new Error(`Exhaustive check failed for ${x}`);
}

function colliderHandlePairsAreEqual(
	a: ColliderHandlePair,
	b: ColliderHandlePair,
) {
	return (a.h1 === b.h1 && a.h2 === b.h2) || (a.h1 === b.h2 && a.h2 === b.h1);
}

interface CollisionSystemOptions {
	/** Colliders that overlap by this distance or less are ignored when calculating collisions. Default value is 0. */
	colliderOverlapThreshold?: number;
	onCollidingEntitiesUpdated?: OnCollidingEntitiesUpdated;
	onValidateEntityCollision?: OnValidateEntityCollision;
}

export class CollisionSystem {
	private _app!: AppBase;
	private _world: RAPIER.World;
	private _eventQueue: RAPIER.EventQueue;
	private _physicsHooks: RAPIER.PhysicsHooks;
	private _connections = new Set<Connection>();
	private _bodyConnections = new Map<RAPIER.RigidBody, Connection>();
	private _entityConnections = new Map<Entity, Connection>();
	private _touchingPairs = new Set<CollisionPair>();
	private _collidingPairs = new Set<CollisionPair>();
	private _collidingBodies = new Set<RAPIER.RigidBody>();
	private _collidingEntities = new Set<Entity>();
	private _colliderMetas = new WeakMap<RAPIER.Collider, ColliderMeta>();
	private _autoStep = false;
	private _renderColliders = false;
	private _renderAabbsForCollidingEntities = false;
	private _colliderOverlapThreshold = 0;
	private _onCollidingEntitiesUpdated?: OnCollidingEntitiesUpdated;
	private _onValidateEntityCollision?: OnValidateEntityCollision;

	private _tempQuat = new Quat();

	constructor() {
		this._world = new RAPIER.World(Vec3.ZERO);
		this._eventQueue = new RAPIER.EventQueue(true);
		this._physicsHooks = this._createPhysicsHooks();
	}

	init(app: AppBase, options?: CollisionSystemOptions): void {
		this._app = app;
		this._app.on("update", this._onUpdate, this);
		this._app.on("prerender", this._renderDebugGeometry, this);
		this._colliderOverlapThreshold = options?.colliderOverlapThreshold ?? 0;
		this._onCollidingEntitiesUpdated = options?.onCollidingEntitiesUpdated;
		this._onValidateEntityCollision = options?.onValidateEntityCollision;
	}

	get rawWorld(): RAPIER.World {
		return this._world;
	}

	get autoStep(): boolean {
		return this._autoStep;
	}

	set autoStep(value: boolean) {
		this._autoStep = value;
	}

	get renderDebugColliders(): boolean {
		return this._renderColliders;
	}

	set renderDebugColliders(value: boolean) {
		this._renderColliders = value;
	}

	get renderAabbsForCollidingEntities(): boolean {
		return this._renderAabbsForCollidingEntities;
	}

	set renderAabbsForCollidingEntities(value: boolean) {
		this._renderAabbsForCollidingEntities = value;
	}

	get collidingEntities(): ReadonlySet<Entity> {
		return this._collidingEntities;
	}

	getEntitiesWithBodies(bodyTypeFilter?: BodyType): Entity[] {
		let connections = [...this._connections];
		if (bodyTypeFilter !== undefined) {
			const bodyType = rapierBodyType[bodyTypeFilter];
			connections = connections.filter(
				({ body }) => body.bodyType() === bodyType,
			);
		}
		return connections.map(({ entity }) => entity);
	}

	hasEntityBody(entity: Entity): boolean {
		return this._entityConnections.has(entity);
	}

	getRawEntityBody(entity: Entity): RAPIER.RigidBody | undefined {
		return this._entityConnections.get(entity)?.body;
	}

	updateEntityBodyFromEntity(entity: Entity): boolean {
		const connection = this._entityConnections.get(entity);
		if (!connection) {
			return false;
		}
		this._updateBodyFromEntity(connection.body, entity);
		return true;
	}

	addEntityBody(entity: Entity, bodyType: BodyType): boolean {
		if (this._entityConnections.has(entity)) {
			console.warn(`Entity with name ${entity.name} already has a body`);
			return false;
		}
		const transform = entity.getWorldTransform();
		const translation = transform.getTranslation();
		const body = this._world.createRigidBody(
			new RAPIER.RigidBodyDesc(rapierBodyType[bodyType])
				.setTranslation(translation.x, translation.y, translation.z)
				.setRotation(entity.getRotation()),
		);

		const onDestroyHandler = () => this.removeEntityBody(entity);
		this._registerConnection({ body, entity, onDestroyHandler });
		entity.on("destroy", onDestroyHandler);

		return true;
	}

	addEntityBodyCollider(
		entity: Entity,
		definition: ColliderDefinition,
		bodyTypeCollisionPairs?: BodyTypeCollisionPair[],
	): boolean {
		const connection = this._entityConnections.get(entity);
		if (!connection) {
			return false;
		}
		let colliderDesc: RAPIER.ColliderDesc;
		const { type } = definition;
		switch (type) {
			case "box":
				colliderDesc = RAPIER.ColliderDesc.cuboid(
					definition.halfExtents.x,
					definition.halfExtents.y,
					definition.halfExtents.z,
				);
				break;
			case "capsule":
				colliderDesc = RAPIER.ColliderDesc.capsule(
					definition.halfHeight,
					definition.radius,
				);
				break;
			case "cone":
				colliderDesc = RAPIER.ColliderDesc.cone(
					definition.halfHeight,
					definition.radius,
				);
				break;
			case "cylinder":
				colliderDesc = RAPIER.ColliderDesc.cylinder(
					definition.halfHeight,
					definition.radius,
				);
				break;
			case "sphere":
				colliderDesc = RAPIER.ColliderDesc.ball(definition.radius);
				break;
			case "plane":
				colliderDesc = new RAPIER.ColliderDesc(
					new RAPIER.HalfSpace({
						x: definition.normal.x,
						y: definition.normal.y,
						z: definition.normal.z,
					}),
				);
				break;
			default:
				exhaustiveCheck(type);
		}
		let collisitonTypes: number = 0;

		if (bodyTypeCollisionPairs) {
			for (const pair of bodyTypeCollisionPairs) {
				collisitonTypes |= rapierActiveCollisionType[pair];
			}
		} else {
			collisitonTypes = RAPIER.ActiveCollisionTypes.DEFAULT;
		}

		const { translation = Vec3.ZERO, rotation = Quat.IDENTITY } = definition;

		colliderDesc
			.setTranslation(translation.x, translation.y, translation.z)
			.setRotation(rotation)
			.setActiveCollisionTypes(collisitonTypes)
			.setActiveEvents(RAPIER.ActiveEvents.COLLISION_EVENTS)
			.setActiveHooks(RAPIER.ActiveHooks.FILTER_INTERSECTION_PAIRS)
			.setSensor(true);
		const collider = this._world.createCollider(colliderDesc, connection.body);
		this._colliderMetas.set(collider, { definitionType: type });

		return true;
	}

	setEntityBodyType(
		entity: Entity,
		bodyType: BodyType,
		recursive = true,
	): void {
		const newType = rapierBodyType[bodyType];

		const processEntity = (node: GraphNode) => {
			if (!(node instanceof Entity)) {
				return;
			}
			const connection = this._entityConnections.get(node);
			if (!connection) {
				return;
			}
			if (connection.body.bodyType() !== newType) {
				connection.body.setBodyType(newType, true);
			}
		};
		if (recursive) {
			// eslint-disable-next-line unicorn/no-array-for-each
			entity.forEach(processEntity);
		} else {
			processEntity(entity);
		}
	}

	removeEntityBody(entity: Entity): boolean {
		const connection = this._entityConnections.get(entity);
		if (!connection) {
			console.warn(
				`Entity with name ${entity.name} is not part of the collision system`,
			);
			return false;
		}
		connection.entity.off("destroy", connection.onDestroyHandler);

		this._removeCollisionPairsForBody(connection.body);
		this._unregisterConnection(connection);
		this._world.removeRigidBody(connection.body);
		return true;
	}

	getEntityBodyContacts(entity: Entity): EntityContact[] {
		const result: EntityContact[] = [];
		const body = this._entityConnections.get(entity)?.body;
		if (!body) {
			return result;
		}

		for (const { b1, b2, h1, h2, contact } of this._collidingPairs) {
			if (!contact || (b1 !== body && b2 !== body)) {
				continue;
			}
			const bodyIsB1 = body === b1;
			const otherEntity = this._bodyConnections.get(bodyIsB1 ? b2 : b1)?.entity;
			if (!otherEntity) {
				continue;
			}
			const { point1, point2, normal1, normal2, distance } = contact;

			const point = vectorToVec3(bodyIsB1 ? point1 : point2);
			const normal = vectorToVec3(bodyIsB1 ? normal1 : normal2);
			const collider = this._world.getCollider(bodyIsB1 ? h1 : h2);
			const colliderMeta = this._colliderMetas.get(collider);
			assert(colliderMeta);

			const otherPoint = vectorToVec3(bodyIsB1 ? point2 : point1);
			const otherNormal = vectorToVec3(bodyIsB1 ? normal2 : normal1);
			const otherCollider = this._world.getCollider(bodyIsB1 ? h2 : h1);
			const otherColliderMeta = this._colliderMetas.get(otherCollider);
			assert(otherColliderMeta);

			result.push({
				distance,
				entity,
				point,
				normal,
				otherEntity,
				otherPoint,
				otherNormal,
				colliderMeta,
				otherColliderMeta,
			});
		}

		return result;
	}

	getEntityBodyCollisions(entity: Entity): Entity[] {
		const connection = this._entityConnections.get(entity);
		if (!connection) {
			return [];
		}
		const collidingBodies = new Set<RAPIER.RigidBody>();

		for (const pair of this._collidingPairs) {
			if (pair.b1 === connection.body) {
				collidingBodies.add(pair.b2);
			} else if (pair.b2 === connection.body) {
				collidingBodies.add(pair.b1);
			}
		}

		return [...collidingBodies]
			.map((body) => this._bodyConnections.get(body)?.entity)
			.filter((entity) => !!entity);
	}

	rayCast(
		ray: Ray,
		options?: {
			maxDistance?: number;
			ignoredEntities?: Entity[];
		},
	): RayCastHit | undefined {
		const { maxDistance = Number.MAX_SAFE_INTEGER, ignoredEntities = [] } =
			options ?? {};
		const ignoredBodies = ignoredEntities
			.map((entity) => this._entityConnections.get(entity)?.body)
			.filter((body) => !!body);
		const filterPredicate = (collider: RAPIER.Collider) => {
			const body = collider.parent();
			return !body || !ignoredBodies.includes(body);
		};
		const intersection = this._world.castRayAndGetNormal(
			new RAPIER.Ray(ray.origin, ray.direction),
			maxDistance,
			true,
			undefined,
			undefined,
			undefined,
			undefined,
			filterPredicate,
		);
		if (!intersection) {
			return undefined;
		}
		const body = intersection.collider.parent();
		const entity = body && this._bodyConnections.get(body)?.entity;
		if (!entity) {
			return undefined;
		}
		const distance = intersection.timeOfImpact;
		const normal = new Vec3(
			intersection.normal.x,
			intersection.normal.y,
			intersection.normal.z,
		);
		const position = ray.direction.clone().mulScalar(distance).add(ray.origin);

		return { entity, normal, position, distance };
	}

	step(): void {
		for (const entry of this._connections) {
			this._updateBodyFromEntity(entry.body, entry.entity);
		}
		this._world.step(this._eventQueue, this._physicsHooks);
		this._drainCollisionEvents();
	}

	private _onUpdate(_dt: number) {
		if (this._autoStep) {
			this.step();
		}
	}

	private _renderDebugGeometry() {
		if (this._renderColliders) {
			const { vertices, colors } = this._world.debugRender();
			this._app.drawLineArrays(
				vertices as unknown as number[],
				colors as unknown as number[],
				true,
			);
		}
		if (this._renderAabbsForCollidingEntities) {
			for (const body of this._collidingBodies) {
				const entity = this._bodyConnections.get(body)?.entity;
				if (!entity) {
					continue;
				}
				const components = [
					...entity.findComponents("model"),
					...entity.findComponents("render"),
				] as (ModelComponent | RenderComponent)[];

				for (const component of components) {
					for (const meshInstance of component.meshInstances ?? []) {
						const box = meshInstance.aabb as BoundingBox;
						this._app.drawWireAlignedBox(
							box.getMin(),
							box.getMax(),
							Color.RED,
							false,
						);
					}
				}
			}
		}
	}

	private _updateBodyFromEntity(body: RAPIER.RigidBody, entity: Entity) {
		const rotation = this._tempQuat;
		getRotation(entity.getWorldTransform(), rotation);

		body.setTranslation(entity.getPosition(), false);
		body.setRotation(rotation, false);
		body.setEnabled(entity.enabled);
	}

	private _drainCollisionEvents() {
		let collidingPairsChanged = false;

		this._eventQueue.drainCollisionEvents((h1, h2, started) => {
			if (started) {
				const b1 = this._world.getCollider(h1).parent();
				const b2 = this._world.getCollider(h2).parent();
				if (b1 && b2) {
					this._touchingPairs.add({ h1, h2, b1, b2 });
				}
			} else {
				const stoppedPair = { h1, h2 };
				for (const pair of this._touchingPairs) {
					if (colliderHandlePairsAreEqual(pair, stoppedPair)) {
						this._touchingPairs.delete(pair);
						if (this._collidingPairs.has(pair)) {
							this._collidingPairs.delete(pair);
							collidingPairsChanged = true;
						}
						break;
					}
				}
			}
		});

		for (const pair of this._touchingPairs) {
			const c1 = this._world.getCollider(pair.h1);
			const c2 = this._world.getCollider(pair.h2);
			const contact = c1.contactCollider(c2, 0);
			const isIntersecting =
				!!contact &&
				Math.abs(contact.distance) > this._colliderOverlapThreshold;

			pair.contact = isIntersecting ? contact : undefined;

			if (isIntersecting !== this._collidingPairs.has(pair)) {
				if (isIntersecting) {
					this._collidingPairs.add(pair);
				} else {
					this._collidingPairs.delete(pair);
				}
				collidingPairsChanged = true;
			}
		}

		if (collidingPairsChanged) {
			this._updateCollidingEntities();
		}
	}

	private _createPhysicsHooks(): RAPIER.PhysicsHooks {
		return {
			filterContactPair(_ch1, _ch2, _bh1, _bh2) {
				return null;
			},
			filterIntersectionPair: (_ch1, _ch2, bh1, bh2) => {
				const b1 = this._world.bodies.get(bh1);
				const b2 = this._world.bodies.get(bh2);
				if (!b1 || !b2) {
					return true;
				}
				const entity1 = this._bodyConnections.get(b1)?.entity;
				const entity2 = this._bodyConnections.get(b2)?.entity;
				if (!entity1 || !entity2) {
					return true;
				}
				return this._onValidateEntityCollision
					? this._onValidateEntityCollision(entity1, entity2)
					: true;
			},
		};
	}

	private _removeCollisionPairsForBody(body: RAPIER.RigidBody) {
		let pairsChanged = false;

		for (const pair of this._touchingPairs) {
			const b1 = this._world.colliders.get(pair.h1)?.parent();
			const b2 = this._world.colliders.get(pair.h2)?.parent();
			if (b1 === body || b2 === body) {
				this._touchingPairs.delete(pair);
				this._collidingPairs.delete(pair);
				pairsChanged = true;
			}
		}

		if (pairsChanged) {
			this._updateCollidingEntities();
		}
	}

	private _updateCollidingEntities() {
		this._collidingBodies.clear();
		for (const pair of this._collidingPairs) {
			this._collidingBodies.add(pair.b1);
			this._collidingBodies.add(pair.b2);
		}
		this._collidingEntities.clear();
		for (const body of this._collidingBodies) {
			const entity = this._bodyConnections.get(body)?.entity;
			if (entity) {
				this._collidingEntities.add(entity);
			}
		}
		this._onCollidingEntitiesUpdated?.();
	}

	private _registerConnection(connection: Connection) {
		assert(!this._bodyConnections.has(connection.body));
		assert(!this._entityConnections.has(connection.entity));

		this._connections.add(connection);
		this._bodyConnections.set(connection.body, connection);
		this._entityConnections.set(connection.entity, connection);
	}

	private _unregisterConnection(connection: Connection) {
		assert(this._bodyConnections.has(connection.body));
		assert(this._entityConnections.has(connection.entity));

		this._connections.delete(connection);
		this._bodyConnections.delete(connection.body);
		this._entityConnections.delete(connection.entity);
	}
}
